import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Paper,
  Checkbox,
  ListItemText,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Collapse,
  Skeleton,
  TextField,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import eventEmitter from "../../../components/utils/eventEmitter";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";

type Device = {
  id: string;
  device_name?: string;
  type: string;
  model?: string;
  vendor?: string;
  location?: string;
  region_name?: string;
  last_scan?: string;
  criticality?: string;
  total_scans?: number;
};

interface DeviceListProps {
  onSelect?: (device: Device) => void;
  initialSelectedDeviceId?: string;
}

const DeviceList: React.FC<DeviceListProps> = ({ onSelect, initialSelectedDeviceId }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [selectedId, setSelectedId] = useState<string>("");
  const [devices, setDevices] = useState<Device[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [vendorFilter, setVendorFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [modelFilter, setModelFilter] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [appliedVendorFilter, setAppliedVendorFilter] = useState<string[]>([]);
  const [appliedTypeFilter, setAppliedTypeFilter] = useState<string[]>([]);
  const [appliedModelFilter, setAppliedModelFilter] = useState<string[]>([]);
  const [appliedRegionFilter, setAppliedRegionFilter] = useState<string[]>([]);
  const [trigger, setTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [vendorOpen, setVendorOpen] = useState<boolean>(false);
  const [typeOpen, setTypeOpen] = useState<boolean>(false);
  const [modelOpen, setModelOpen] = useState<boolean>(false);
  const [regionOpen, setRegionOpen] = useState<boolean>(false);
  const [vendors, setVendors] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);

  const FILTER_STORAGE_KEY = "device_list_filters";
  const SELECTED_DEVICE_KEY = "device_list_selected_device";


  useEffect(() => {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setAppliedVendorFilter(parsed.vendor || []);
        setAppliedTypeFilter(parsed.type || []);
        setAppliedModelFilter(parsed.model || []);
        setAppliedRegionFilter(parsed.region || []);
        setVendorFilter(parsed.vendor || []);
        setTypeFilter(parsed.type || []);
        setModelFilter(parsed.model || []);
        setRegionFilter(parsed.region || []);
        setSearchText(parsed.searchText || "");
      } catch (e) {
        console.warn("Failed to restore filters", e);
      }
    }

    const savedSelected = localStorage.getItem(SELECTED_DEVICE_KEY);
    if (savedSelected) {
      setSelectedId(savedSelected);
    }
  }, []);

  // ✅ Fetch Devices
  useEffect(() => {
    let ignore = false;
    const fetchDevices = async () => {
      if (ignore) return;
      setIsLoading(true);
      try {
        const resp = await axiosInstance.get(END_POINTS.ASSET_CONFIG_DIFF);
        const data = resp?.data?.data;
        let list: any[] = [];

        // Prefer explicit device_list when available
        if (Array.isArray((data as any)?.device_list)) {
          list = (data as any).device_list;
        } else if (Array.isArray(data)) {
          // If API returns an array directly
          list = data as any[];
        } else if (data && typeof data === "object") {
          // If API returns a single device object, wrap it as an array
          list = [data];
        }

        const mapped: Device[] = list.map((it: any) => ({
          id: it.id || it.device_id || "",
          device_name: it.device_name,
          model: it.model,
          vendor: it.vendor,
          type: it.type || it.device_type || "",
          location: it.location || it.region_name || "Unknown",
          region_name: it.region_name,
        }));

        if (!ignore) {
          setDevices(mapped);
          const vendorList = mapped
            .map((m) => m.vendor)
            .filter((v): v is string => Boolean(v));
          const typeList = mapped
            .map((m) => m.type)
            .filter((t): t is string => Boolean(t));
          const modelList = mapped
            .map((m) => m.model)
            .filter((m): m is string => Boolean(m));
          const regionList = mapped
            .map((m) => m.region_name)
            .filter((r): r is string => Boolean(r));

          setVendors([...new Set(vendorList)]);
          setTypes([...new Set(typeList)]);
          setModels([...new Set(modelList)]);
          setRegions([...new Set(regionList)]);

          // Only auto-select first device if no device is currently selected
          // and no initialSelectedDeviceId is provided
          if (mapped.length && !selectedId && !initialSelectedDeviceId) {
            setSelectedId(mapped[0].id);
            onSelect?.(mapped[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching devices:", err);
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    fetchDevices();
    return () => {
      ignore = true;
    };
  }, [trigger]); // Removed initialSelectedDeviceId from dependencies to prevent re-fetch

  // Handle initialSelectedDeviceId separately after devices are loaded
  // This effect runs when initialSelectedDeviceId is provided (e.g., navigating back from ShowConfigDiffPage)
  // It should take priority even if a device was already auto-selected
  useEffect(() => {
    if (!initialSelectedDeviceId || !devices.length) return;

    const foundDevice = devices.find(d =>
      d.id === initialSelectedDeviceId ||
      d.id?.toString() === initialSelectedDeviceId ||
      d.device_name === initialSelectedDeviceId
    );

    if (foundDevice && foundDevice.id !== selectedId) {
      setSelectedId(foundDevice.id);
      onSelect?.(foundDevice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedDeviceId, devices]);

  // ✅ Filter Logic
  const filteredDevices = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return devices.filter((d) => {
      if (q && !(d.device_name || "").toLowerCase().includes(q)) return false;
      if (appliedVendorFilter.length && !appliedVendorFilter.includes(d.vendor || ""))
        return false;
      if (appliedTypeFilter.length && !appliedTypeFilter.includes(d.type)) return false;
      if (appliedModelFilter.length && !appliedModelFilter.includes(d.model || ""))
        return false;
      if (
        appliedRegionFilter.length &&
        !appliedRegionFilter.includes(d.region_name || "")
      )
        return false;
      return true;
    });
  }, [
    devices,
    searchText,
    appliedVendorFilter,
    appliedTypeFilter,
    appliedModelFilter,
    appliedRegionFilter,
  ]);

  const isSameArray = (a: string[], b: string[]) =>
    a.length === b.length && a.every(v => b.includes(v));

  const hasChangesFromApplied =
    !isSameArray(vendorFilter, appliedVendorFilter) ||
    !isSameArray(typeFilter, appliedTypeFilter) ||
    !isSameArray(modelFilter, appliedModelFilter) ||
    !isSameArray(regionFilter, appliedRegionFilter);

  const hasAnySelectedFilters =
    vendorFilter.length > 0 ||
    typeFilter.length > 0 ||
    modelFilter.length > 0 ||
    regionFilter.length > 0;

  const hasAppliedFilters =
    appliedVendorFilter.length > 0 ||
    appliedTypeFilter.length > 0 ||
    appliedModelFilter.length > 0 ||
    appliedRegionFilter.length > 0;

  const canApply = hasChangesFromApplied;
  const canReset = hasAnySelectedFilters || hasAppliedFilters;
  const canClearAll = hasAnySelectedFilters || hasAppliedFilters;

  useEffect(() => {
    if (showFilters) {
      // when panel opens → restore draft from applied
      setVendorFilter(appliedVendorFilter);
      setTypeFilter(appliedTypeFilter);
      setModelFilter(appliedModelFilter);
      setRegionFilter(appliedRegionFilter);
    }
  }, [
    showFilters,
    appliedVendorFilter,
    appliedTypeFilter,
    appliedModelFilter,
    appliedRegionFilter,
  ]);


  // Show red dot on the filter icon whenever any applied filter/search is active
  const showFilterDot = hasAppliedFilters;

  // ✅ Handlers
  const handleDeviceClick = useCallback(
    (device: Device) => {
      // Prevent re-selecting the same device (avoids infinite loading)
      if (device.id === selectedId) return;

      setSelectedId(device.id);
      localStorage.setItem(SELECTED_DEVICE_KEY, device.id);
      onSelect?.(device);
    },
    [onSelect, selectedId]
  );
  const handleToggleFilter = (
    filterType: "vendor" | "type" | "model" | "region",
    value: string
  ) => {
    const setFilter =
      filterType === "vendor"
        ? setVendorFilter
        : filterType === "type"
          ? setTypeFilter
          : filterType === "model"
            ? setModelFilter
            : setRegionFilter;
    setFilter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const handleApplyFilters = () => {
    const nextVendor = [...vendorFilter];
    const nextType = [...typeFilter];
    const nextModel = [...modelFilter];
    const nextRegion = [...regionFilter];

    setAppliedVendorFilter(nextVendor);
    setAppliedTypeFilter(nextType);
    setAppliedModelFilter(nextModel);
    setAppliedRegionFilter(nextRegion);
    setShowFilters(false);

    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        vendor: nextVendor,
        type: nextType,
        model: nextModel,
        region: nextRegion,
        searchText,
      })
    );

    // Compute the filtered devices with the new filters
    const q = searchText.trim().toLowerCase();
    const newFilteredDevices = devices.filter((d) => {
      if (q && !(d.device_name || "").toLowerCase().includes(q)) return false;
      if (nextVendor.length && !nextVendor.includes(d.vendor || "")) return false;
      if (nextType.length && !nextType.includes(d.type)) return false;
      if (nextModel.length && !nextModel.includes(d.model || "")) return false;
      if (nextRegion.length && !nextRegion.includes(d.region_name || "")) return false;
      return true;
    });

    // Auto-select the first device in the filtered list only if it's different from current selection
    if (newFilteredDevices.length > 0) {
      const firstDevice = newFilteredDevices[0];
      // Only trigger onSelect if the device actually changed to avoid unnecessary loading
      if (firstDevice.id !== selectedId) {
        setSelectedId(firstDevice.id);
        onSelect?.(firstDevice);
      }
    }
  };

  const handleClearFilters = () => {
    setVendorFilter([]);
    setTypeFilter([]);
    setModelFilter([]);
    setRegionFilter([]);
    setAppliedVendorFilter([]);
    setAppliedTypeFilter([]);
    setAppliedModelFilter([]);
    setAppliedRegionFilter([]);

    localStorage.removeItem(FILTER_STORAGE_KEY);

    // Auto-select the first device after clearing filters (show all devices) only if different
    const q = searchText.trim().toLowerCase();
    const allDevices = q ? devices.filter((d) => (d.device_name || "").toLowerCase().includes(q)) : devices;
    if (allDevices.length > 0) {
      const firstDevice = allDevices[0];
      // Only trigger onSelect if the device actually changed to avoid unnecessary loading
      if (firstDevice.id !== selectedId) {
        setSelectedId(firstDevice.id);
        onSelect?.(firstDevice);
      }
    }
  };

  useEffect(() => {
    const handleFilterSaved = () => {
      setIsLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  const handleResetAll = () => {
    setVendorFilter([]);
    setTypeFilter([]);
    setModelFilter([]);
    setRegionFilter([]);
    setAppliedVendorFilter([]);
    setAppliedTypeFilter([]);
    setAppliedModelFilter([]);
    setAppliedRegionFilter([]);
    setSearchText("");
    setVendorOpen(true);
    setTypeOpen(true);
    setModelOpen(true);
    setRegionOpen(true);

    localStorage.removeItem(FILTER_STORAGE_KEY); // 🔥 clear storage
    localStorage.removeItem(SELECTED_DEVICE_KEY);

    // Auto-select the first device after reset (show all devices) only if different
    if (devices.length > 0) {
      const firstDevice = devices[0];
      // Only trigger onSelect if the device actually changed to avoid unnecessary loading
      if (firstDevice.id !== selectedId) {
        setSelectedId(firstDevice.id);
        onSelect?.(firstDevice);
      }
    }
  };

  // ✅ Render Filter Section
  const renderFilterSection = (
    title: string,
    items: string[],
    selectedItems: string[],
    isOpen: boolean,
    onToggle: () => void,
    filterType: "vendor" | "type" | "model" | "region"
  ) => (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={onToggle}>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={items.length > 0 && selectedItems.length === items.length}
              tabIndex={-1}
              disableRipple
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                const setFilter =
                  filterType === "vendor"
                    ? setVendorFilter
                    : filterType === "type"
                      ? setTypeFilter
                      : filterType === "model"
                        ? setModelFilter
                        : setRegionFilter;

                const allSelected =
                  items.length > 0 &&
                  items.every((item) => selectedItems.includes(item));

                setFilter(allSelected ? [] : [...items]);
              }}
            />
          </ListItemIcon>
          <ListItemText primary={title} />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 4 }}>
          {items.map((item) => (
            <ListItem key={item} dense disablePadding>
              <ListItemButton onClick={() => handleToggleFilter(filterType, item)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedItems.includes(item)}
                    disableRipple
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleFilter(filterType, item)}
                  />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );

  // ✅ Render Device Item
  const renderDeviceItem = (device: Device, index: number) => {
    const isSelected = device.id === selectedId;
    return (
      <Box
        key={device.id}
        onClick={() => handleDeviceClick(device)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1.5,
          py: 1.2,
          borderBottom: "1px solid rgba(16,24,40,0.05)",
          bgcolor: isSelected
            ? "rgba(58,42,218,0.06)"
            : index % 2
              ? "transparent"
              : "rgba(99,102,241,0.03)",
          cursor: isSelected ? "default" : "pointer",
          transition: "all 0.25s ease",
          "&:hover": {
            bgcolor: isSelected
              ? "rgba(58,42,218,0.08)"
              : "rgba(99,102,241,0.05)",
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: isSelected ? "primary.main" : "text.primary",
            }}
          >
            {device.device_name || "-"}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {device.region_name || "-"}
          </Typography>
        </Box>
        <Typography sx={{ color: "primary.main", fontSize: 12 }}>
          {device.vendor
            ? device.model
              ? `${device.vendor} - ${device.model}`
              : device.vendor
            : device.model || "-"}
        </Typography>
      </Box>
    );
  };

  // ✅ Main Return
  return (
    <Paper
      elevation={0}
      sx={{
        width: isExpanded ? 300 : 56,
        minWidth: isExpanded ? 300 : 56,
        flexShrink: 0,
        borderRadius: 2,
        border: "1px solid rgba(16,24,40,0.06)",
        overflow: "hidden",
        bgcolor: "background.paper",
        transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: isExpanded ? 1 : 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: isExpanded ? "space-between" : "center",
          borderBottom: "1px solid rgba(16,24,40,0.06)",
          transition: "all 0.3s ease",
        }}
      >
        {isExpanded && (
          <>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText("")}>
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                disableUnderline: true,
              }}
              sx={{
                flex: 1,
                mr: 1,
                color: theme.palette.text.primary,
                borderRadius: "6px",
                border: "1px solid",
                borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.24)",
                },
                "&:focus-within": {
                  borderColor: isDarkMode ? theme.palette.primary.main : "",
                  boxShadow: isDarkMode ? `0 0 0 2px ${theme.palette.primary.main}25` : "",
                  backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                },
                "& .MuiInputBase-root": {
                  height: "32px !important",
                  minHeight: "32px !important",
                  alignItems: "center",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.primary,
                  lineHeight: "32px",
                  padding: "0px !important",
                  fontSize: 13,
                  fontWeight: 500,
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.text.secondary,
                  opacity: 0.8,
                },
                "& fieldset": { border: "none" },
              }}
            />
            <Box sx={{ position: "relative", ml: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setShowFilters((s) => !s)}
                sx={{
                  "&:hover": { bgcolor: "action.hover" },
                  transition: "all 0.2s ease",
                }}
                aria-label="toggle filters"
              >
                <FilterAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {showFilterDot && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "green",
                    border: (theme) => `2px solid ${theme.palette.background.paper}`,
                  }}
                />
              )}
            </Box>
          </>
        )}

        {/* Expand / Collapse Icon (No Color) */}
        <IconButton
          size="small"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label="toggle sidebar width"
          sx={{
            ml: isExpanded ? 0.5 : 0,
            color: "text.primary",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "action.hover",
              transform: "scale(1.1)",
            },
          }}
        >
          {isExpanded ? (
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      {/* Filters or List */}
      {isExpanded && (showFilters ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: 740,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(16,24,40,0.08)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setShowFilters(false)}
                sx={{
                  bgcolor: "action.hover",
                  "&:hover": { bgcolor: "action.selected" },
                  transition: "0.2s",
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Filters
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={handleClearFilters}
              disabled={!canClearAll}
              sx={{
                textTransform: "none",
                color: "primary.main",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Clear All
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 1.5,
              py: 1,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.15)"
                    : "rgba(0,0,0,0.15)",
                borderRadius: "10px",
              },
            }}
          >
            <List
              disablePadding
              sx={{
                "& .MuiListItemButton-root": { pl: 1 },
                "& .MuiListItemIcon-root": { minWidth: 32 },
              }}
            >
              {renderFilterSection(
                "Vendors",
                vendors,
                vendorFilter,
                vendorOpen,
                () => setVendorOpen((s) => !s),
                "vendor"
              )}
              <Divider sx={{ my: 1 }} />
              {renderFilterSection(
                "Asset Type",
                types,
                typeFilter,
                typeOpen,
                () => setTypeOpen((s) => !s),
                "type"
              )}
              <Divider sx={{ my: 1 }} />
              {renderFilterSection(
                "OS",
                models,
                modelFilter,
                modelOpen,
                () => setModelOpen((s) => !s),
                "model"
              )}
              <Divider sx={{ my: 1 }} />
              {renderFilterSection(
                "Regions",
                regions,
                regionFilter,
                regionOpen,
                () => setRegionOpen((s) => !s),
                "region"
              )}
            </List>
          </Box>
          <Divider />
          <Box
            sx={{
              p: 1.25,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetAll}
              disabled={!canReset}
              sx={{ textTransform: "none", fontSize: 13, px: 2, mr: 2 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={!canApply}
              sx={{ textTransform: "none", fontSize: 13, px: 2.5, fontWeight: 600 }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={(theme) => ({
            maxHeight: 635,
            overflowY: "auto",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(0,0,0,0.15)",
              borderRadius: "10px",
            },
          })}
        >
          {isLoading ? (
            <Box sx={{ p: 1.5 }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1.5,
                    py: 1.2,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={16} />
                  </Box>
                  <Skeleton variant="text" width="30%" height={16} />
                </Box>
              ))}
            </Box>
          ) : filteredDevices.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                No Assets found
              </Typography>
            </Box>
          ) : (
            filteredDevices.map(renderDeviceItem)
          )}
        </Box>
      ))}
    </Paper>
  );
};

export type { Device };
export default DeviceList;
